import * as React from 'react';
import {useEffect} from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import {useLocation} from "react-router-dom";
import ScrollTop from './Shared/Components/ScrollTop';
import Fab from '@mui/material/Fab';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SGDSubMenu from "./Shared/Components/Layout/menu/SGDSubMenu";
import SGDMainMenu from "./Shared/Components/Layout/menu/SGDMainMenu";
import SGDDashboardHeaderMenu from "./Modules/Dashboard/DashboardHeaderMenu/SGDDashboardHeaderMenu";
import SGDDashboardSubHeaderMenu from './Modules/Dashboard/SGDDashboardSubHeaderMenu';
import useAppBackgroundSetup from "./Config/Hooks/AppBackgroundSetupHook";
import useAppLayoutSetup from "./Config/Hooks/AppLayoutSetupHook";
import AppRoutes from "./Config/AppRoutes";
import {useAppDispatch, useAppSelector} from "./Config/Hooks";
import {Storage} from "./Utils/StorageUtils";
import {logout} from "./Config/Reducers/AuthenticationReducer";
import {useSnackbar} from "notistack";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import IconButton from "@mui/material/IconButton";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {toggleSubMenu} from "./Config/Reducers/LayoutReducer";
import SGDAppBarPage from "./Shared/Components/Layout/SGDAppBarPage";
import {AppBar, Toolbar, useTheme} from "@mui/material";
import {WebSocketProvider} from "./Modules/Login/hooks/useWebSocket";
import { useAuth } from 'react-oidc-context';
import { useAuthToken } from './Config/Hooks/useAuthToken';
import Breadcrumb from "./Shared/Components/Layout/menu/Breadcrumb";
import {
    isDashboardPage,
    isCommunicationEquipmentsPage,
    isCommunicationTeleobjectsPage,
    isSettingsUsersPage,
    isAuditCommandsPage, isDashboardOverviewPerformancePage, isDashboardPowerPlantInvertersPage
} from './Utils/RouteUtils';
import SGDFooter from "./Shared/Components/Layout/SGDFooter";
import AIAgentHost from "./Modules/AIAgent/AIAgentHost";
import { AUTHORITIES } from "./Config/Constants";
import { hasPermission } from "./Shared/Auth/AuthenticationUtil";
import './Shared/ThemeOld/fonts.css';

export default function App(props: any) {
    const location = useLocation();
    const appBackgroundSetup = useAppBackgroundSetup();
    const appLayoutSetup = useAppLayoutSetup();
    const { token, isAuthenticated } = useAuthToken();
    const AUTH_TOKEN_KEY = 'api-token-key';
    const dispatch = useAppDispatch();
    const auth = useAuth();
    // Layout Settings
    const openMainMenu = useAppSelector((state: any) => state.layout.openMainMenu);
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const contentPagePaddingTop = useAppSelector((state: any) => state.layout.contentPagePaddingTop);
    const account = useAppSelector((state: any) => state.authentication.account);
    const authoritySet = Array.isArray(account?.authoritySet) ? account.authoritySet : [];
    const canUseAiChat = hasPermission(authoritySet, [
        AUTHORITIES.VIEW_AI_CHAT,
    ]);
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const currentRoute = location.pathname;
    const { selectedPowerPlant, selectedSkid, selectedEquipment } = useAppSelector(
        (state: any) => state.dashboard
    );
    const aiAgentScreenSelections = React.useMemo(() => ({
        powerStationName: selectedPowerPlant?.name,
        skidName: selectedSkid?.name,
        equipmentName: selectedEquipment?.name,
    }), [selectedPowerPlant?.name, selectedSkid?.name, selectedEquipment?.name]);
    
    const verifyTokenValidity = (token: string) => {
        try {
            const tokenData = JSON.parse(atob(token.split('.')[1]));
            const expiresIn = tokenData.exp * 1000;
            return expiresIn > Date.now();
        } catch (error) {
            return false;
        }
    }

    const getMainBackGroundColor = (currentRoute: string) => {
        if(currentRoute !== '/' && theme.palette.mode === "light"){
            return "#f7f7f7"
        }
        return ''
    }

    const shouldShowFooter = (route: string): boolean => {
        return route !== '/' && isAuthenticated;
    }

    useEffect(() => {
        const tokenCheckInterval = setInterval(() => {
            const token = Storage.local.get(AUTH_TOKEN_KEY);
            if (token) {
                try {
                    const tokenData = JSON.parse(atob(token.split('.')[1]));
                    const expiresIn = tokenData.exp * 1000;
                    const now = Date.now();
                    
                    // Only invalidate if token is expired or will expire in next 5 minutes
                    if (expiresIn < now + 300000) {
                        enqueueSnackbar("Session expired!", {variant: 'error'});
                        dispatch(logout());
                        auth.signoutRedirect();
                    }
                } catch (error) {
                    console.error('Error validating token:', error);
                    // Don't automatically logout on validation error
                }
            }
        }, 30000); // Check every 30 seconds instead of 10
        
        return () => clearInterval(tokenCheckInterval);
    }, [dispatch, enqueueSnackbar, auth.isAuthenticated]);

    useEffect(() => {
        appLayoutSetup();
    }, [appLayoutSetup]);

    useEffect(() => {
        appLayoutSetup();
    },[appLayoutSetup]);

    useEffect( () => {
        appLayoutSetup();
        appBackgroundSetup();
    },[appBackgroundSetup, appLayoutSetup, location]);

    const [open, setOpen] = React.useState(false);

    useEffect(() => {
        setOpen(openSubMenu);
    }, [openSubMenu]);

    const menuToogle = () => {
        dispatch(toggleSubMenu());
    }
    const defaultProps = {
        borderColor: 'text.primary',
        borderRadius: 5
    };

    const isMedianScreen = window.innerWidth <= 520;    

    return (
        <WebSocketProvider>
        <Box id="back-to-top-anchor"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: currentRoute !== '/' ? theme.palette.background.default : '',
                    minHeight: currentRoute !== '/' ? '100vh' : '',
                    paddingTop: 10,
                }}
        >
            <CssBaseline/>

            {/*Menu Settings*/}
            <SGDAppBarPage handleThemeMode={props.handleThemeMode}/>
            <SGDSubMenu/>
            <SGDMainMenu/>
            <SGDDashboardHeaderMenu/>
            <SGDDashboardSubHeaderMenu/>
            { openMainMenu && currentRoute !== '/' &&
                <Box
                    position="fixed"
                    top={107}
                    left={open ? 380 : 65}
                    zIndex="modal"
                    sx={{
                        transition: "transform 0.2s linear",
                    }}
                    {...defaultProps}
                    style={{alignItems: "center", justifyContent: 'center', width: 35, height: 35}}
                >
                    <IconButton
                        aria-label="open drawer"
                        title={open ? "Recolher" : "Expandir"}
                        onClick={menuToogle}
                        sx={{
                            backgroundColor: "#051F36",
                            alignItems: "center",
                            justifyContent: 'center',
                            transform: 'scale(0.5)',
                            opacity: 0.7,
                            color: "#FFFFFF",
                            '&:hover': {
                                opacity: 1,
                                backgroundColor: "#051F36",
                                transform: 'scale(0.7)',
                                transition: "all 0.2s linear"
                            }
                        }}
                    >
                        {open ?
                            <ChevronLeftIcon/>
                            :
                            <ChevronRightIcon/>

                        }
                    </IconButton>
                </Box>
            }

            <CssBaseline/>

            <Box component="main"
                    style={{
                        backgroundColor: getMainBackGroundColor(currentRoute),
                        paddingBottom: 24,
                        paddingTop: isMedianScreen ? 96 : contentPagePaddingTop + 8,
                    }}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: {
                            xs: "initial", md: (
                                openSubMenu ? (
                                    openMainMenu ? `${400}px` : `${80}px`
                                ) : (
                                    openMainMenu ? `${80}px` : `${0}` // Set margin to 50 if submenu closed and main menu open
                                )
                            )
                        },
                    }}
            >
                <Box component="main"
                    style={{
                        backgroundColor: getMainBackGroundColor(currentRoute),
                        paddingLeft: 24,
                        paddingRight: 24,
                    }}>
                {
                    ((isDashboardPage() && !isDashboardOverviewPerformancePage() && !isDashboardPowerPlantInvertersPage()) &&
                     <Toolbar style={{ paddingLeft: 24, paddingRight: 0, paddingTop: 0, paddingBottom: 24 }}>
                        <Breadcrumb />
                    </Toolbar>) ||
                    ((isSettingsUsersPage() || isCommunicationEquipmentsPage() || isCommunicationTeleobjectsPage()) &&
                        <Toolbar style={{ paddingLeft: 25, paddingRight: 0, paddingTop: 0, paddingBottom: 0, marginTop: -60 }}>
                            <Breadcrumb />
                        </Toolbar>
                    ) ||
                    ( isAuditCommandsPage() &&
                        <Toolbar style={{ paddingLeft: 10, paddingRight: 0, paddingTop: 0, paddingBottom: 0, marginTop: -70 }}>
                            <Breadcrumb />
                        </Toolbar>
                    )

                }
           
                {/*App Routes*/}
                <AppRoutes/>

                {/*how to get what route*/}
                <ScrollTop {...props} >
                    <Fab size="small" aria-label="scroll back to top">
                        <KeyboardArrowUpIcon/>
                    </Fab>
                </ScrollTop>
                {isAuthenticated && canUseAiChat && (
                    <AIAgentHost
                        accountResolved={!!isAuthenticated}
                        authoritySet={authoritySet}
                        screenSelections={aiAgentScreenSelections}
                    />
                )}
                </Box>
                {shouldShowFooter(currentRoute) && 
                    <Box style={{
                        marginTop: 'auto',
                        paddingTop: '48px',
                        width: '100%',
                        }}>
                        <SGDFooter />
                    </Box>
                } 
            </Box>
        </Box>
        </WebSocketProvider>
    );
}
